import * as React from 'react';
import * as styles from './TimeStreamToolbox.scss';
import { DragHandle } from 'owa-timestream-asset-sdk';
import { Icon } from '@fluentui/react/lib/Icon';
import { isCurrentCultureRightToLeft } from 'owa-localize';
import { observer } from 'mobx-react-lite';
import { shouldShowExpandedToolbox } from '../viewstate';
import getToolAriaLabel from '../util/getToolAriaLabel';
import { useKeydownSelection } from 'owa-react-hooks/lib/useKeydownSelection';
import classNames from 'classnames';

export default observer(function TimeStreamToolboxButton({
    title,
    getToolName,
    getTooltip,
    iconName,
    iconNameRTL,
    showTitle,
    isDraggable,
    disabled,
    onClick,
}: {
    title?: string;
    getToolName?: () => string;
    getTooltip?: () => string;
    iconName: string;
    iconNameRTL?: string;
    showTitle?: boolean;
    isDraggable?: boolean;
    disabled?: boolean;
    onClick: (ev: React.MouseEvent<Element> | React.KeyboardEvent<Element>) => void;
}) {
    const toolName = title || (getToolName ? getToolName() : '');
    const tooltip = getTooltip ? getTooltip() : toolName;
    const isRTL = isCurrentCultureRightToLeft();
    const actualIconName = (isRTL && iconNameRTL) || iconName;
    const shouldShowTitle = showTitle || shouldShowExpandedToolbox();

    const onKeyDown = useKeydownSelection(onClick, [onClick]);

    // We might want to show disabled buttons; for now let's just hide it.
    if (disabled) {
        return null;
    }

    return (
        /**
         * Technically this should be a <button> element, not a <div>. But in Edge, IE, and Firefox, <button> elements are not draggable.
         * This is also why a separate keyDown is necessary- a button binds onClick to the keydown by default, but divs do not.
         * See: https://github.com/react-dnd/react-dnd/issues/803
         */
        <div
            className={classNames(styles.toolContainer, {
                [styles.overflowTool]: shouldShowTitle,
            })}
            title={tooltip}
            aria-label={getToolAriaLabel(tooltip)}
            onClick={onClick}
            role="menuitem"
            tabIndex={0}
            data-is-focusable={true}
            onKeyDown={onKeyDown}>
            <div className={styles.toolIcon}>
                {isDraggable && <DragHandle size={12} className={styles.dragHandle} />}
                <Icon iconName={actualIconName} />
            </div>
            {shouldShowTitle && (
                <div className={styles.toolTitle} aria-hidden={true}>
                    {toolName}
                </div>
            )}
        </div>
    );
});
